import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstant from "../api";
import { getActivityLog, ActivityLogQuery } from "../api/endpoints";

export interface ActivityLogRow {
    id: number;
    action: string;
    category: string;
    severity: "major" | "minor";
    actor_user_id: number | null;
    actor_usertype: string | null;
    actor_display_name: string | null;
    brand_id: number | null;
    brand_order_id: number | null;
    brand_project_id: number | null;
    s3_file_id: number | null;
    summary: string;
    metadata: Record<string, any> | null;
    slack_notified: boolean;
    created_at: string;
}

// A feed is anchored at one level; 'global' passes no anchor (admin only).
export type ActivityLevel = "project" | "order" | "brand" | "file" | "global";

export interface UseActivityFeedOptions {
    level: ActivityLevel;
    id?: number | string | null;
    category?: string; // comma-separated
    severity?: string; // 'major' to show only major
    // Additional exact-id filters applied on top of the level anchor (set by the
    // user via the id inputs / clickable row ids). null = not filtering.
    orderId?: number | null;
    projectId?: number | null;
    fileId?: number | null;
    limit?: number;
    // Poll interval (ms) — minor events no longer ping Slack, so a light poll
    // keeps the feed feeling live. 0/undefined disables.
    pollMs?: number;
}

const anchorParam = (
    level: ActivityLevel,
    id?: number | string | null
): ActivityLogQuery => {
    switch (level) {
        case "project":
            return { brand_project_id: id ?? undefined };
        case "order":
            return { brand_order_id: id ?? undefined };
        case "brand":
            return { brand_id: id ?? undefined };
        case "file":
            return { s3_file_id: id ?? undefined };
        default:
            return {};
    }
};

/**
 * Fetches a paginated activity feed for one anchor (project/order/brand/file)
 * or the global admin feed, plus any user-applied id filters. Handles "load
 * more" and optional light polling. Every filter change refetches a fresh
 * server-side batch (page 1) — it does NOT filter already-loaded rows.
 */
export function useActivityFeed({
    level,
    id,
    category,
    severity,
    orderId,
    projectId,
    fileId,
    limit = 25,
    pollMs,
}: UseActivityFeedOptions) {
    const [rows, setRows] = useState<ActivityLogRow[]>([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Monotonic fetch generation: only the latest fetch may commit state, so an
    // out-of-order response, a stale poll tick, a load-more landing on a changed
    // filter, or a resolve-after-unmount is dropped instead of clobbering the
    // current filter's rows.
    const genRef = useRef(0);

    const needsAnchor = level !== "global";
    const missingAnchor =
        needsAnchor && (id === undefined || id === null || id === "");

    const fetchPage = useCallback(
        async (targetPage: number, replace: boolean) => {
            if (missingAnchor) return;
            const myGen = ++genRef.current;
            setLoading(true);
            setError(null);
            try {
                const url = getActivityLog({
                    ...anchorParam(level, id),
                    ...(orderId ? { brand_order_id: orderId } : {}),
                    ...(projectId ? { brand_project_id: projectId } : {}),
                    ...(fileId ? { s3_file_id: fileId } : {}),
                    category: category || undefined,
                    severity: severity || undefined,
                    page: targetPage,
                    limit,
                });
                const { data, status } = await axiosInstant.get(url);
                // A newer fetch (filter change, poll tick, load-more, or unmount)
                // superseded this one — drop its result rather than overwrite.
                if (myGen !== genRef.current) return;
                if (status === 200) {
                    // customControl wraps the payload under `.data`.
                    const payload = (data && data.data) || data || {};
                    const newRows: ActivityLogRow[] = payload.rows || [];
                    setCount(payload.count || 0);
                    setRows((prev) =>
                        replace ? newRows : [...prev, ...newRows]
                    );
                    setPage(targetPage);
                }
            } catch (e: any) {
                if (myGen !== genRef.current) return;
                setError((e && e.message) || "Failed to load activity");
            } finally {
                if (myGen === genRef.current) setLoading(false);
            }
        },
        [
            missingAnchor,
            level,
            id,
            category,
            severity,
            orderId,
            projectId,
            fileId,
            limit,
        ]
    );

    // Reload from page 1 whenever the anchor or filters change.
    useEffect(() => {
        fetchPage(1, true);
        // Invalidate any in-flight request when the filters change or the
        // component unmounts, so a late response can't commit stale rows.
        return () => {
            genRef.current += 1;
        };
    }, [fetchPage]);

    // Light polling to refresh the first page. Paused once the user has paged
    // past 1 via "Load more" — otherwise a tick would call fetchPage(1, true)
    // and discard every loaded page.
    useEffect(() => {
        if (!pollMs || missingAnchor || page > 1) return undefined;
        const t = setInterval(() => fetchPage(1, true), pollMs);
        return () => clearInterval(t);
    }, [pollMs, missingAnchor, page, fetchPage]);

    const hasMore = rows.length < count;
    const loadMore = useCallback(() => {
        if (!loading && hasMore) fetchPage(page + 1, false);
    }, [loading, hasMore, page, fetchPage]);
    const refresh = useCallback(() => fetchPage(1, true), [fetchPage]);

    return { rows, count, loading, error, hasMore, loadMore, refresh };
}
